<template>
  <div
    class="bom-results row relative-position full-height no-wrap q-px-lg q-pt-md q-pb-md"
    :class="isDrawerOpened ?
      'details-drawer-opened' :
      'details-drawer-closed'"
  >
    <div class="col column no-wrap full-height">
      <div class="row justify-between items-center">
        <!-- Validation results tabs -->
        <div class="col-auto">
          <error-types-tabs
            v-model="activeBomValidationErrorType"
            :errors-total-sizes="validationResultsTotalSize[activeBomValidationResultType.key] || {}"
          ></error-types-tabs>
        </div>

        <!-- Validation error type select -->
        <div class="col-auto">
          <result-types-select
            v-if="activeBomValidationErrorType !== 'incompleteElements'"
            v-model="activeBomValidationResultType"
            :results-total-sizes="validationResultsTotalSize"
            @value:has-changed="saveCurrentElements"
          ></result-types-select>
        </div>
      </div>

      <!-- CORE limit warning banner -->
      <bom-validation-results-elements-info
        v-show="showInfo.incomplete && activeBomValidationErrorType === BOM_VALIDATION_ERROR_TYPES.INCOMPLETE.key"
        :info-data="BOM_VALIDATION_ERROR_TYPES_MESSAGE.INCOMPLETE"
      >
      </bom-validation-results-elements-info>
      <!-- Precondition info banner -->
      <bom-validation-results-elements-info
        v-show="bomValidationResults.precondition?.length > 0"
        :info-data="BOM_VALIDATION_PRE_CONDITION_MESSAGE"
      >
      </bom-validation-results-elements-info>

      <!-- Validation results by error type -->
      <mi-tab-panels v-model="activeBomValidationErrorType" class="col">
        <!-- Unused KVs -->
        <result-elements :items="currentDisplayedElements" :name="BOM_VALIDATION_ERROR_TYPES.UNUSED.key">
          <template #default="{ item }">
            <unused-element
              :key="item.id"
              :item="item"
              :encoded-business-name="bomValidationResults.encodedBusinessName"
              :component-validation-id="bomValidationResults.id"
            ></unused-element>
          </template>
        </result-elements>

        <!-- Incomplete elements -->
        <result-elements
          :new-version-available="currentDisplayedElements.length > 0"
          :items="currentDisplayedElements"
          :results="filteredExpressions"
          :name="BOM_VALIDATION_ERROR_TYPES.INCOMPLETE.key"
          :validation-id="bomValidationId"
          :product-model-id="bomValidationResults.productModelType === PM_TYPE.PRODUCT_MODEL?
            bomValidationResults.encodedBusinessName: bomValidationResults.productModelId"
          :bom-validation-results="bomValidationResults"
          :pm-type="bomValidationResults.productModelType"
          :component-id="bomValidationResults.componentId"
          :filter-results="getBomValidationResults"
          :duplicated-elements="duplicatedExpressionsFormated"
          :show-validation-similar-results="showValidationSimilarResults"
          :number-of-cases="numberOfCases"
        >
          <template #default="{ item, index }">
            <bom-validation-results-elements-incomplete
              v-if="showValidationResultsNewVersion && !showValidationSimilarResults"
              :key="item.id"
              :item="item"
              :item-index="index"
            >
            </bom-validation-results-elements-incomplete>
            <incomplete-element-old-version
              v-if="!showValidationResultsNewVersion"
              :key="item.id"
              :item="item"
            >
            </incomplete-element-old-version>
          </template>
          <template #results="{ results, index }">
            <bom-validation-similar-solutions
              v-if="showValidationSimilarResults"
              :key="results.id"
              :item-index="index"
              :item="results"
            >
            </bom-validation-similar-solutions>
          </template>
        </result-elements>

        <!-- Ambiguous elements -->
        <result-elements
          :new-version-available="currentDisplayedElements.length > 0"
          :items="showValidationResultsNewVersion ?
            currentAmbiguousDisplayedElementsGroupByRule
            : currentDisplayedElements"
          :validation-id="bomValidationId"
          :product-model-id="bomValidationResults.encodedBusinessName"
          :bom-validation-results="bomValidationResults"
          :name="BOM_VALIDATION_ERROR_TYPES.AMBIGUOUS.key"
        >
          <template #default="{ item }">
            <bom-validation-results-elements-ambiguous
              v-if="showValidationResultsNewVersion"
              :key="item.id"
              :item="item"
            ></bom-validation-results-elements-ambiguous>
            <ambiguous-element-old-version
              v-else
              :key="item.id"
              :item="item"
            ></ambiguous-element-old-version>
          </template>
        </result-elements>
      </mi-tab-panels>
    </div>

    <!-- BOM validation details -->
    <div class="col-auto q-pl-lg">
      <validation-details
        :bom-validation-details="bomValidationResults"
        @is-drawer-opened="(value) => isDrawerOpened = value"
      ></validation-details>
    </div>

    <!-- Spinner -->
    <mi-inner-loading class="bg-white" :showing="isBomValidationReceiving">
      <mi-spinner size="70px"></mi-spinner>
      <h6 class="q-mt-lg q-mb-none"> Loading BOM validation. Please wait ... </h6>
    </mi-inner-loading>
  </div>
</template>

<script>
  import { getBOMValidation, getBOMValidationTarFilter } from '@/api'
  import { transformSimilarResults } from '@/utils/formatExplanation'

  import {
    BOM_VALIDATION_ERROR_TYPES,
    BOM_VALIDATION_ERROR_TYPES_MESSAGE,
    BOM_VALIDATION_RESULT_TYPES,
    SHOW_NEW_VERSION_PROVIDER,
    BOM_VALIDATION_PRE_CONDITION_MESSAGE, PM_TYPE, SHOW_SIMILAR_RESULTS_PROVIDER
  } from '@/constants'

  import { SET_EXPORT_DATA_VISIBILITY } from '@/store/modules/validations/mutationTypes'
  import { createNamespacedHelpers } from 'vuex'
  import BomValidationResultsElementsInfo from
    '@/components/validations/bom/validation-results/elements/BomValidationResultsElementsInfo.vue'
  import { ref, provide } from 'vue'
  import { LocalStorage } from 'quasar'
  import BomValidationSimilarSolutions
    from '@/components/validations/bom/validation-results/elements/BomValidationSimilarSolutions.vue'
  import ErrorTypesTabs from './BomValidationResultsErrorTypesTabs.vue'
  import ResultTypesSelect from './BomValidationResultsTypesSelect.vue'
  import ValidationDetails from './BomValidationResultsDetails.vue'
  import ResultElements from './elements/BomValidationResultsElements.vue'
  import AmbiguousElementOldVersion from './elements/BomValidationResultsElementsAmbiguousOldVersion.vue'
  import IncompleteElementOldVersion from './elements/BomValidationResultsElementsIncompleteOldVersion.vue'
  import UnusedElement from './elements/BomValidationResultsElementsUnused.vue'
  import BomValidationResultsElementsAmbiguous from './elements/BomValidationResultsElementsAmbiguous.vue'
  import BomValidationResultsElementsIncomplete from './elements/BomValidationResultsElementsIncomplete.vue'

  const { mapMutations } = createNamespacedHelpers('validations')

  export default {
    name: 'BomValidationResults',
    components: {
      BomValidationResultsElementsIncomplete,
      BomValidationResultsElementsAmbiguous,
      BomValidationResultsElementsInfo,
      AmbiguousElementOldVersion,
      ErrorTypesTabs,
      IncompleteElementOldVersion,
      ResultElements,
      ResultTypesSelect,
      UnusedElement,
      ValidationDetails,
      BomValidationSimilarSolutions
    },
    setup() {
      const lastSavedVersion = LocalStorage.getItem(process.env.VUE_APP_STORAGE_KEY_VALIDATIONS_NEW_VERSION)
      const showValidationResultsNewVersion = ref(lastSavedVersion !== null ? lastSavedVersion : true)
      const showValidationSimilarResults = ref(false)
      provide(SHOW_NEW_VERSION_PROVIDER, showValidationResultsNewVersion)
      provide(SHOW_SIMILAR_RESULTS_PROVIDER, showValidationSimilarResults)

      const isDrawerOpened = ref(true)

      return {
        showValidationResultsNewVersion,
        isDrawerOpened,
        showValidationSimilarResults
      }
    },
    data: () => ({
      BOM_VALIDATION_ERROR_TYPES,
      BOM_VALIDATION_RESULT_TYPES,
      activeBomValidationErrorType: BOM_VALIDATION_ERROR_TYPES.AMBIGUOUS.key,
      activeBomValidationResultType: BOM_VALIDATION_RESULT_TYPES.COMPONENT_VARIANT,
      isBomValidationReceiving: false,
      bomValidationResults: {},
      showInfo: { incomplete: false },
      bomValidationId: '',
      duplicatedExpressionsFormated: [],
      filteredExpressions: [],
      numberOfCases: 0
    }),
    computed: {
      PM_TYPE() {
        return PM_TYPE
      },
      BOM_VALIDATION_PRE_CONDITION_MESSAGE() {
        return BOM_VALIDATION_PRE_CONDITION_MESSAGE
      },
      BOM_VALIDATION_ERROR_TYPES_MESSAGE() {
        return BOM_VALIDATION_ERROR_TYPES_MESSAGE
      },
      allExplanations() {
        if (!this.activeBomValidationResultType?.key) return []
        const currentActiveResultType = this.bomValidationResults[this.activeBomValidationResultType.key] || {}
        const elements = currentActiveResultType[this.activeBomValidationErrorType] || []

        return elements.map(item => transformSimilarResults(item.explanation)).filter(Boolean)
      },
      getNumberOfCases() {
        const currentActiveResultType = this.bomValidationResults[this.activeBomValidationResultType?.key] || {}
        return currentActiveResultType[this.activeBomValidationErrorType]?.length || 0
      },
      currentDisplayedElements() {
        const currentActiveResultType = this.bomValidationResults[this.activeBomValidationResultType?.key] || {}

        return currentActiveResultType[this.activeBomValidationErrorType] || []
      },
      currentAmbiguousDisplayedElementsGroupByRule() {
        const mappedCurrentAmbiguousElements = new Map()

        for (const item of this.currentDisplayedElements) {
          const key = `${ item.ambiguous1Id2 }} ${ item.ambiguous1Name } -
          ${ item.ambiguous2Id2 } ${ item.ambiguous2Name }`

          const existing = mappedCurrentAmbiguousElements.get(key)

          if (existing) {
            existing.explanation.push(item.explanation)
          }
          else {
            mappedCurrentAmbiguousElements.set(key, {
              ...item,
              explanation: [item.explanation]
            })
          }
        }

        return Array.from(mappedCurrentAmbiguousElements.values())
      },
      validationResultsTotalSize() {
        const elementsSizes = {}

        Object.values(BOM_VALIDATION_RESULT_TYPES).forEach(({ key: validationResultType } = {}) => {
          const currentValidationResultType = this.bomValidationResults[validationResultType] || {}

          elementsSizes[validationResultType] = { totalSize: 0 }

          Object.values(BOM_VALIDATION_ERROR_TYPES).forEach(({ key: validationErrorType } = {}) => {
            const errorTypeTotalSize = currentValidationResultType[validationErrorType]?.length || 0

            elementsSizes[validationResultType][validationErrorType] = errorTypeTotalSize
            elementsSizes[validationResultType].totalSize += errorTypeTotalSize
          })
        })

        return elementsSizes
      }
    },
    watch: {
      showValidationSimilarResults(newVal) {
        if (newVal) {
          this.uniqueExpressions()
        }
      }
    },
    async created() {
      const { id: bomValidationId } = this.$route.params || {}

      this.bomValidationId = bomValidationId

      if (bomValidationId) {
        await this.getBomValidationResults(bomValidationId)
      }
    },
    methods: {
      ...mapMutations({ SET_EXPORT_DATA_VISIBILITY }),

      getCommonExpressions(allExplanations) {
        if (!allExplanations.length) return []

        const expressionSets = allExplanations.map(exp => new Set(exp.expressions))

        return [...expressionSets[0]].filter(expr => expressionSets.every(set => set.has(expr)))
      },

      removeCommonExpressions(allExplanations, commonExpressions) {
        const commonSet = new Set(commonExpressions)
        const filteredExplanations = allExplanations.map(explanation => ({
          ...explanation,
          expressions: explanation.expressions.filter(expr => !commonSet.has(expr))
        }))

        return filteredExplanations.map(explanation => ({
          ...explanation.expressions.map(item => transformSimilarResults(item))
        }))
      },

      uniqueExpressions() {
        const commonExpressions = this.getCommonExpressions(this.allExplanations)
        const filteredExplanations = this.removeCommonExpressions(this.allExplanations, commonExpressions)
        this.numberOfCases = this.getNumberOfCases

        // Return only expressions that appear more than once in all cases
        this.duplicatedExpressionsFormated = commonExpressions.map(item => transformSimilarResults(item))

        this.filteredExpressions = [...new Set(filteredExplanations)]
      },

      async getBomValidationResults(validationId = '', params = {}, kvsList = []) {
        this.isBomValidationReceiving = true

        try {
          if (kvsList.length > 0) {
            this.bomValidationResults = await getBOMValidationTarFilter(validationId, params, kvsList)
          }
          else {
            this.bomValidationResults = await getBOMValidation(validationId, params)
          }
          this.saveCurrentElements()
          this.uniqueExpressions()
        }
        finally {
          this.isBomValidationReceiving = false
          this.containsAllPossibleCombinations()
        }
      },
      saveCurrentElements() {
        const activeBomValidationTypeData = this.bomValidationResults[this.activeBomValidationResultType.key]
        if (!activeBomValidationTypeData) return
        const {
          ambiguousElements,
          incompleteElements,
          unusedElements
        } = activeBomValidationTypeData

        const displayExportData = !!(ambiguousElements.length || incompleteElements.length || unusedElements.length)

        this.SET_EXPORT_DATA_VISIBILITY(displayExportData)
        this.containsAllPossibleCombinations()
      },
      containsAllPossibleCombinations() {
        const { containsAllIncompleteCombinations } = this.bomValidationResults
        const resultTypeData = this.bomValidationResults[this.activeBomValidationResultType.key]
        if (!resultTypeData) return
        this.showInfo.incomplete = containsAllIncompleteCombinations === false
          && resultTypeData.incompleteElements?.length > 0
      }
    }
  }
</script>

<style lang="scss">
  .or-divider {
    color: $mi-red-500;
  }

  .line {
    display: flex;
    flex-direction: row;
    gap: 8px;
    color: $mi-anthracite-500;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.3rem;
  }

  .number {
    display: flex;
    justify-content: flex-end;
    min-width: 16px;
  }

  .explanation-wrapper {
    margin: 8px 20px;
    border-radius: .25rem;
  }

  .explanation-content {
    padding: 8px;
    background: #eaedf366;
  }

  .details-drawer-opened {
    padding-right: 354px;
  }

  .details-drawer-closed {
    padding-right: 57px;
  }
</style>
